import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Button, ConfigProvider } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import backgroundImage from '../../assets/images/wallpaper.jpg';
// The BatchGenius lockup (bars glyph + wordmark) — the teal sibling of the Nexgensis corporate mark.
// Trimmed to its ink bounds from CompanyLogo1.1.png; the wordmark is white, so it needs a dark ground.
import batchGeniusLockup from '../../assets/images/BatchGeniusLockup.png';
import BatchGeniusMark from '@/components/BatchGeniusMark';
import { getAntdTheme } from '@/themes/antd-theme';

// ==============================|| AUTHENTICATION - WRAPPER ||============================== //

const AuthWrapper = ({ children }) => {
  // Force light mode for login page
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');

    // Remove dark class when login page mounts
    root.classList.remove('dark');

    // Restore dark class when login page unmounts (if it was dark before)
    return () => {
      if (wasDark) {
        root.classList.add('dark');
      }
    };
  }, []);

  // Get light theme configuration
  const lightTheme = getAntdTheme(false);

  return (
    <ConfigProvider theme={lightTheme}>
  <div className="relative min-h-screen w-full flex items-center justify-end">
    {/* Full Background Image */}
    <img
      src={backgroundImage}
      alt="Background"
      className="absolute inset-0 w-full h-full object-cover"
      style={{ zIndex: 0 }}
    />

    {/* Subtle Overlay */}
    <div
      className="absolute inset-0"
      style={{
        zIndex: 1,
        background: 'linear-gradient(to right, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%)',
      }}
    />

    {/* Top Header */}
    <div
      className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 py-4"
      style={{
        background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, transparent 100%)',
      }}
    >
      {/* EBMR Logo/Header */}
      <div className="flex items-center gap-4">
        <img
          src={batchGeniusLockup}
          alt="BatchGenius"
          className="h-20 w-auto drop-shadow-lg"
          style={{ aspectRatio: '909 / 533' }}
        />
        <span className="hidden sm:block text-white/80 text-sm tracking-wider self-end pb-3">
          Electronic Batch Manufacturing Record
        </span>
      </div>

      {/* Home Button */}
      <Button
        type="primary"
        icon={<HomeOutlined />}
        onClick={() => (window.location.href = '/')}
        style={{
          backgroundColor: '#204448',
          borderColor: '#204448',
          boxShadow: '0 4px 14px rgba(32, 68, 72, 0.4)',
        }}
        className="hover:opacity-90"
      >
        Home
      </Button>
    </div>

    {/* Login Form - Right Side */}
    <div className="relative z-10 w-full lg:w-1/2 min-h-screen flex items-center justify-center p-6">
      <div
        className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8"
        style={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
        }}
      >
        {/* Mobile Branding */}
        <div className="lg:hidden flex flex-col items-center text-center mb-6">
          <BatchGeniusMark size={44} className="rounded-xl mb-2" />
          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">BatchGenius</h1>
          <p className="text-sm text-gray-500 tracking-wider">Electronic Batch Manufacturing Record</p>
        </div>

        {children}

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-xs">
            © {new Date().getFullYear()} BatchGenius by Nexgensis. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </div>
  </ConfigProvider>
  );
};

AuthWrapper.propTypes = {
  children: PropTypes.node,
};

export default AuthWrapper;
